import { defineStore } from "pinia";

const FIREBASE_BATTLE_GAMES_URL = import.meta.env
  .VITE_FIREBASE_BATTLE_GAMES_URL;

const normalizeType = (game) => {
  if (!game || typeof game !== "object") return game;
  const typeCandidate = game.type ?? game["type:"] ?? game["type::"];
  if (typeCandidate && typeCandidate !== game.type) {
    return { ...game, type: typeCandidate };
  }
  return game;
};

const normalizeResponseToList = (data) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") return Object.values(data);
  return [];
};

export const useGameStore = defineStore("game", () => {
  const games = ref([]);
  const isLoadingGames = ref(false);
  const gamesLoadError = ref("");
  const gamesLoadedAt = ref(0);

  const gamesById = computed(() => {
    const map = {};
    games.value.forEach((game) => {
      const id = game?.id;
      if (!id) return;
      map[id] = game;
    });
    return map;
  });

  const hasLoadedGames = computed(
    () => gamesLoadedAt.value > 0 && games.value.length > 0,
  );

  const getGameById = (id) => {
    if (!id) return null;
    return gamesById.value[id] || null;
  };

  const fetchGames = async () => {
    if (isLoadingGames.value) return;
    isLoadingGames.value = true;
    gamesLoadError.value = "";

    try {
      const res = await fetch(FIREBASE_BATTLE_GAMES_URL, { cache: "no-cache" });
      if (!res.ok) throw new Error(`Failed to load games (${res.status})`);
      const data = await res.json();

      games.value = normalizeResponseToList(data)
        .map(normalizeType)
        .filter((game) => game && typeof game === "object" && game.id);

      gamesLoadedAt.value = Date.now();
    } catch (err) {
      console.error(err);
      games.value = [];
      gamesLoadedAt.value = 0;
      gamesLoadError.value = err?.message || "Cannot load games";
    } finally {
      isLoadingGames.value = false;
    }
  };

  const ensureGamesLoaded = async () => {
    if (hasLoadedGames.value || isLoadingGames.value) return;
    await fetchGames();
  };

  return {
    games,
    gamesById,
    isLoadingGames,
    gamesLoadError,
    gamesLoadedAt,
    hasLoadedGames,
    fetchGames,
    ensureGamesLoaded,
    getGameById,
  };
});
